import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Chip, TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Alert, Pagination, IconButton,
  InputAdornment, Tooltip,
} from '@mui/material';
import {
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  ClearAll as ClearIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtCurrency = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const fmtNum = (v, decimals = 0) => {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const PO_STATUS_COLOR = {
  Open:               'primary',
  Printed:            'info',
  Partial:            'warning',
  'Partially Received': 'warning',
  Complete:           'success',
  Unknown:            'default',
};

const LINE_STATUS_COLOR = {
  Open:               'primary',
  'Partially Received': 'warning',
  Complete:           'success',
  Unknown:            'default',
};

// ── Summary Card ──────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color }) {
  return (
    <Card elevation={2}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}
          textTransform="uppercase" letterSpacing={0.5}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700} color={color || 'text.primary'} mt={0.5}>
          {value}
        </Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// ── Line Items (expanded detail row) ─────────────────────────────────────────

function PODetailRow({ ponumber, colSpan }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/warehouse/purchase-orders/${ponumber}`)
      .then(r => setDetail(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [ponumber]);

  if (loading) return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 3, textAlign: 'center', bgcolor: '#f9fbff' }}>
        <CircularProgress size={22} />
      </TableCell>
    </TableRow>
  );

  if (error) return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ bgcolor: '#f9fbff', pl: 4 }}>
        <Alert severity="error" sx={{ my: 1 }}>Failed to load PO detail: {error}</Alert>
      </TableCell>
    </TableRow>
  );

  if (!detail) return null;

  const { header, lines } = detail;

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ p: 0, bgcolor: '#f9fbff', borderBottom: '2px solid #e0e7ff' }}>
        <Box sx={{ p: 2 }}>

          {/* Header meta strip */}
          <Grid container spacing={2} mb={2}>
            {header.reference && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">Reference</Typography>
                <Typography variant="body2" fontWeight={600}>{header.reference}</Typography>
              </Grid>
            )}
            {header.terms && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">Terms</Typography>
                <Typography variant="body2" fontWeight={600}>{header.terms}</Typography>
              </Grid>
            )}
            {header.fob && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">FOB</Typography>
                <Typography variant="body2" fontWeight={600}>{header.fob}</Typography>
              </Grid>
            )}
            {header.requested_by && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">Requested By</Typography>
                <Typography variant="body2" fontWeight={600}>{header.requested_by}</Typography>
              </Grid>
            )}
            {header.stock_location && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">Location</Typography>
                <Typography variant="body2" fontWeight={600}>{header.stock_location}</Typography>
              </Grid>
            )}
            {header.delivery_date && (
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary" display="block">Delivery Date</Typography>
                <Typography variant="body2" fontWeight={600}>{fmtDate(header.delivery_date)}</Typography>
              </Grid>
            )}
          </Grid>

          {header.comment && (
            <Box mb={2} p={1.5} sx={{ bgcolor: '#fffde7', borderRadius: 1, border: '1px solid #fff9c4' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>Comments</Typography>
              <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>{header.comment}</Typography>
            </Box>
          )}

          {/* Line items table */}
          <Typography variant="subtitle2" fontWeight={700} mb={1}>
            Line Items ({lines.length})
          </Typography>

          {lines.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No line items found.</Typography>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#eef2ff', fontSize: '0.75rem' } }}>
                    <TableCell>Line</TableCell>
                    <TableCell>Item Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty Ordered</TableCell>
                    <TableCell align="right">Qty Received</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Ext. Value</TableCell>
                    <TableCell align="right">Value Received</TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => {
                    // Colour-code by receipt status
                    const pct = line.qty_ordered > 0 ? line.qty_received / line.qty_ordered : 0;
                    const rowBg =
                      pct >= 1       ? 'rgba(46,125,50,0.06)'  :   // green — fully received
                      pct > 0        ? 'rgba(245,124,0,0.06)'  :   // amber — partial
                                       'rgba(198,40,40,0.04)';      // red   — nothing received

                    return (
                      <TableRow key={i} sx={{ bgcolor: rowBg }}>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{line.line}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>
                          {line.item_code || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{line.description || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {fmtNum(line.qty_ordered, 2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          <Tooltip title={`${fmtNum(line.qty_received, 2)} / ${fmtNum(line.qty_ordered, 2)}`}>
                            <span style={{
                              fontWeight: pct >= 1 ? 700 : 'inherit',
                              color: pct >= 1 ? '#2e7d32' : pct > 0 ? '#f57c00' : '#c62828',
                            }}>
                              {fmtNum(line.qty_received, 2)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {fmtCurrency(line.unit_rate)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {fmtCurrency(line.rev_value || line.orig_value)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.78rem' }}>
                          {fmtCurrency(line.value_received)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem' }}>{line.job || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={line.status}
                            color={LINE_STATUS_COLOR[line.status] || 'default'}
                            size="small"
                            sx={{ fontSize: '0.68rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Line colour legend */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'rgba(46,125,50,0.35)' }} />
              <Typography variant="caption" color="text.secondary">Fully received</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'rgba(245,124,0,0.35)' }} />
              <Typography variant="caption" color="text.secondary">Partially received</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'rgba(198,40,40,0.25)' }} />
              <Typography variant="caption" color="text.secondary">Not yet received</Typography>
            </Box>
          </Box>

        </Box>
      </TableCell>
    </TableRow>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PurchaseOrders() {
  // ── filter state
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [job, setJob]                 = useState('');
  const [jobInput, setJobInput]       = useState('');
  const [vendor, setVendor]           = useState('');
  const [vendorInput, setVendorInput] = useState('');
  const [status, setStatus]           = useState('all');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');

  // ── data state
  const [pos, setPos]               = useState([]);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);
  const [page, setPage]             = useState(1);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [expandedPo, setExpandedPo] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (col) => {
    if (sortBy === col) {
      if (sortDir === 'desc') setSortDir('asc');
      else { setSortBy(''); setSortDir('desc'); }
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const sortedPos = [...pos].sort((a, b) => {
    if (!sortBy) return 0;
    let av = a[sortBy], bv = b[sortBy];
    if (['orig_amount', 'revised_amount', 'received_amount', 'line_count'].includes(sortBy)) {
      av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
    } else if (sortBy === 'date') {
      av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0;
    } else {
      av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // debounce timers
  const searchTimer  = useRef(null);
  const jobTimer     = useRef(null);
  const vendorTimer  = useRef(null);

  const fetchPOs = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { page, limit: 50, status };
    if (search)   params.search    = search;
    if (job)      params.job       = job;
    if (vendor)   params.vendor    = vendor;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;

    api.get('/warehouse/purchase-orders', { params })
      .then(r => {
        setPos(r.data.purchase_orders || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
        setSummary(r.data.summary || null);
      })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [page, search, job, vendor, status, dateFrom, dateTo]);

  useEffect(() => { fetchPOs(); }, [fetchPOs]);

  // Reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [search, job, vendor, status, dateFrom, dateTo]);

  // ── debounced setters
  const handleSearchChange = (v) => {
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v), 500);
  };
  const handleJobChange = (v) => {
    setJobInput(v);
    clearTimeout(jobTimer.current);
    jobTimer.current = setTimeout(() => setJob(v), 500);
  };
  const handleVendorChange = (v) => {
    setVendorInput(v);
    clearTimeout(vendorTimer.current);
    vendorTimer.current = setTimeout(() => setVendor(v), 500);
  };

  const clearFilters = () => {
    setSearchInput(''); setSearch('');
    setJobInput('');    setJob('');
    setVendorInput(''); setVendor('');
    setStatus('all');
    setDateFrom(''); setDateTo('');
    setPage(1);
  };

  const hasFilters = searchInput || jobInput || vendorInput || status !== 'all' || dateFrom || dateTo;

  const toggleExpand = (ponumber) => {
    setExpandedPo(prev => prev === ponumber ? null : ponumber);
  };

  const COL_SPAN = 10;

  return (
    <Box>
      {/* ── Page header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>Purchase Orders</Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>Search and review POs from Sage 300</Typography>
      </Box>

      {/* ── Summary cards */}
      {summary && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="POs Found" value={total.toLocaleString()} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total PO Value" value={fmtCurrency(summary.total_revised || summary.total_orig)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard label="Total Received" value={fmtCurrency(summary.total_received)} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard
              label="Outstanding"
              value={fmtCurrency((summary.total_revised || summary.total_orig) - summary.total_received)}
              color={(((summary.total_revised || summary.total_orig) - summary.total_received) > 0) ? 'warning.main' : undefined}
            />
          </Grid>
        </Grid>
      )}

      {/* ── Filter bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="PO#, vendor, item, job…"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Job #"
              placeholder="e.g. 23-001"
              value={jobInput}
              onChange={e => handleJobChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Vendor"
              placeholder="Vendor name…"
              value={vendorInput}
              onChange={e => handleVendorChange(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={e => setStatus(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Complete</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
          </Grid>
          {hasFilters && (
            <Grid item xs={6} sm={3} md="auto">
              <Tooltip title="Clear all filters">
                <IconButton onClick={clearFilters} size="small" color="default">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ── Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load purchase orders: {error}</Alert>
      )}

      {/* ── Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#0D2C54', color: '#fff', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ width: 40, bgcolor: '#0D2C54' }} />
                {[
                  { key: 'po_number', label: 'PO #' },
                  { key: 'date', label: 'Date' },
                  { key: 'vendor_name', label: 'Vendor' },
                  { key: 'job_number', label: 'Job #' },
                  { key: 'description', label: 'Description' },
                  { key: 'revised_amount', label: 'PO Value', align: 'right' },
                  { key: 'received_amount', label: 'Received', align: 'right' },
                  { key: 'status', label: 'Status' },
                  { key: 'line_count', label: 'Lines', align: 'right' },
                ].map(col => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    <TableSortLabel
                      active={sortBy === col.key}
                      direction={sortBy === col.key ? sortDir : 'asc'}
                      onClick={() => handleSort(col.key)}
                      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && pos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COL_SPAN} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" mt={1}>Loading purchase orders…</Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading && pos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COL_SPAN} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    No purchase orders found{hasFilters ? ' matching your filters' : ''}.
                  </TableCell>
                </TableRow>
              )}

              {sortedPos.map((po) => {
                const isExpanded = expandedPo === po.po_number;
                const receivedPct = po.revised_amount > 0
                  ? po.received_amount / po.revised_amount
                  : po.orig_amount > 0
                    ? po.received_amount / po.orig_amount
                    : 0;

                return (
                  <React.Fragment key={po.po_number}>
                    <TableRow
                      hover
                      onClick={() => toggleExpand(po.po_number)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'rgba(17,68,122,0.05)' : 'inherit',
                        '&:hover': { bgcolor: isExpanded ? 'rgba(17,68,122,0.08)' : undefined },
                      }}
                    >
                      {/* expand toggle */}
                      <TableCell sx={{ py: 0.5, pl: 1 }}>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); toggleExpand(po.po_number); }}>
                          {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {po.po_number}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(po.date)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {po.vendor_name || po.supplier || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {po.job || '—'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                          {po.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {fmtCurrency(po.revised_amount || po.orig_amount)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={`${(receivedPct * 100).toFixed(1)}% received`}>
                          <span style={{
                            color: receivedPct >= 1 ? '#2e7d32' : receivedPct > 0 ? '#f57c00' : '#c62828',
                            fontWeight: receivedPct >= 1 ? 700 : 'inherit',
                          }}>
                            {fmtCurrency(po.received_amount)}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={po.status}
                          color={PO_STATUS_COLOR[po.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{po.line_count}</TableCell>
                    </TableRow>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <PODetailRow ponumber={po.po_number} colSpan={COL_SPAN} />
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination + count footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderTop: '1px solid #e0e0e0', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${total.toLocaleString()} PO${total !== 1 ? 's' : ''} total`}
          </Typography>
          {pages > 1 && (
            <Pagination
              count={pages}
              page={page}
              onChange={(_, v) => { setPage(v); setExpandedPo(null); }}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}
